/**
 * src/store.js
 * Redux store configuration using Redux Toolkit and Redux Persist.
 * Handles UI state, authentication, call sessions, and message history.
 */

import { configureStore, createSlice } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';

// Import centralized API provider
import { Get as ProviderGet } from './provider';
import { clearAllResourceCaches } from './utils/resourceCacheStore';
import { compareByTmCreateThenId } from './utils/timestampCompare';

const DELEGATE_SESSION_KEY = 'delegate_session'

const loadDelegateFromSession = () => {
  const empty = { isActive: false, token: null, agentInfo: null, customerId: null, expire: null }
  try {
    const raw = sessionStorage.getItem(DELEGATE_SESSION_KEY)
    if (!raw) return empty
    const data = JSON.parse(raw)
    if (!data.expire || new Date(data.expire) <= new Date()) {
      sessionStorage.removeItem(DELEGATE_SESSION_KEY)
      return empty
    }
    return { isActive: true, ...data }
  } catch {
    return empty
  }
}

/**
 * UI State: Handles sidebar visibility and layout settings
 */
const uiSlice = createSlice({
    name: 'ui',
    initialState: {
        sidebarUnfoldable: false,
        sidebarSections: {
            voice: false,
            messaging: false,
            ai: false,
            platform: false,
            operations: false,
            platformManagement: false,
        },
    },
    reducers: {
        update: (state, action) => {
            // Update state with all payload properties
            Object.assign(state, action.payload);
        },
        setSidebarUnfoldable: (state, action) => {
            state.sidebarUnfoldable = action.payload;
        },
        toggleSectionCollapse: (state, action) => {
            const sectionId = action.payload;
            state.sidebarSections[sectionId] = !state.sidebarSections[sectionId];
        },
        setSectionCollapse: (state, action) => {
            const { sectionId, isOpen } = action.payload;
            state.sidebarSections[sectionId] = isOpen;
        },
    },
});

export const { update, setSidebarUnfoldable, toggleSectionCollapse, setSectionCollapse } = uiSlice.actions;

/**
 * Theme State: Handles theme preference (light/dark/system)
 */
const themeSlice = createSlice({
    name: 'theme',
    initialState: {
        mode: 'system', // 'light', 'dark', or 'system'
    },
    reducers: {
        setTheme: (state, action) => {
            state.mode = action.payload;
            localStorage.setItem('voipbin-theme', action.payload);
        },
    },
});

export const { setTheme } = themeSlice.actions;
export const ThemeSetMode = (mode) => ({ type: themeSlice.actions.setTheme.type, payload: mode });

/**
 * AI Messages State: Stores AI-assisted call interaction history
 */
const aiMessagesSlice = createSlice({
    name: 'aiMessages',
    initialState: { data: {} },
    reducers: {
        setWithAIcallID: (state, action) => {
            const { aicall_id, data } = action.payload;
            state.data[aicall_id] = data;
        },
        addWithAIcallID: (state, action) => {
            const { aicall_id, data } = action.payload;
            const existing = state.data[aicall_id] || [];
            const idx = existing.findIndex((m) => m.id === data.id);
            if (idx >= 0) {
                // Replace intermediate entry with final message
                if (existing[idx]._intermediate) {
                    existing[idx] = data;
                }
                // Otherwise skip (already have final version)
                return;
            }
            state.data[aicall_id] = [data, ...existing];
        },
        mergeWithAIcallID: (state, action) => {
            const { aicall_id, data } = action.payload;
            const existing = state.data[aicall_id] || [];
            const byId = new Map(existing.map((m) => [m.id, m]));
            data.forEach((m) => byId.set(m.id, m));
            state.data[aicall_id] = Array.from(byId.values()).sort(
                (a, b) => (b.tm_create || '').localeCompare(a.tm_create || '')
            );
        },
        appendIntermediateWithAIcallID: (state, action) => {
            const { aicall_id, data } = action.payload;
            const existing = state.data[aicall_id] || [];
            const idx = existing.findIndex((m) => m.id === data.id);
            if (idx >= 0) {
                const msg = existing[idx];
                // Only append if sequence is newer
                if (data.sequence > (msg._seq ?? -1)) {
                    existing[idx] = {
                        ...msg,
                        content: (msg.content || '') + (data.content || ''),
                        _seq: data.sequence,
                    };
                }
            } else {
                // New intermediate message — prepend
                state.data[aicall_id] = [
                    { ...data, _intermediate: true, _seq: data.sequence },
                    ...existing,
                ];
            }
        },
    },
});

export const aiMessagesReducer = aiMessagesSlice.reducer;
export const { setWithAIcallID, addWithAIcallID, mergeWithAIcallID, appendIntermediateWithAIcallID } = aiMessagesSlice.actions;
export const AImessagesSetWithAIcallID = (aicallID, data) => ({ type: aiMessagesSlice.actions.setWithAIcallID.type, payload: { aicall_id: aicallID, data } });
export const AImessagesAddWithAIcallID = (aicallID, data) => ({ type: aiMessagesSlice.actions.addWithAIcallID.type, payload: { aicall_id: aicallID, data } });
export const AImessagesMergeWithAIcallID = (aicallID, data) => ({ type: aiMessagesSlice.actions.mergeWithAIcallID.type, payload: { aicall_id: aicallID, data } });
export const AImessagesAppendIntermediateWithAIcallID = (aicallID, data) => ({ type: aiMessagesSlice.actions.appendIntermediateWithAIcallID.type, payload: { aicall_id: aicallID, data } });

/**
 * Transcripts State: Stores transcription data from calls
 */
const transcriptsSlice = createSlice({
    name: 'transcripts',
    initialState: { data: {} },
    reducers: {
        setWithTranscribeID: (state, action) => {
            const { chatroom_id, data } = action.payload;
            state.data[chatroom_id] = data;
        },
        addWithTranscribeID: (state, action) => {
            const { chatroom_id, data } = action.payload;
            if (state.data[chatroom_id]) {
                state.data[chatroom_id] = [data, ...state.data[chatroom_id]];
            } else {
                state.data[chatroom_id] = [data];
            }
        },
    },
});

export const { setWithTranscribeID, addWithTranscribeID } = transcriptsSlice.actions;
export const TranscriptsSetWithTranscribeID = (transcribeID, data) => ({ type: transcriptsSlice.actions.setWithTranscribeID.type, payload: { chatroom_id: transcribeID, data } });
export const TranscriptsAddWithTranscribeID = (transcribeID, data) => ({ type: transcriptsSlice.actions.addWithTranscribeID.type, payload: { chatroom_id: transcribeID, data } });

/**
 * Auth State: Stores logged-in agent profile and JWT token
 */
const authSlice = createSlice({
    name: 'auth',
    initialState: {
        agentInfo: null,
        token: null,
    },
    reducers: {
        setAgentInfo: (state, action) => {
            state.agentInfo = action.payload;
            localStorage.setItem("agent_info", JSON.stringify(action.payload));
        },
        setToken: (state, action) => {
            state.token = action.payload;
            localStorage.setItem("token", action.payload);
        },
        clear: (state) => {
            state.agentInfo = null;
            state.token = null;
            localStorage.removeItem("agent_info");
            localStorage.removeItem("token");
            clearAllResourceCaches();
        },
    },
});

export const { setAgentInfo, setToken, clear } = authSlice.actions;
export const AuthSetAgentInfo = (agentInfo) => ({ type: authSlice.actions.setAgentInfo.type, payload: agentInfo });
export const AuthSetToken = (token) => ({ type: authSlice.actions.setToken.type, payload: token });
export const AuthClear = () => ({ type: authSlice.actions.clear.type });

/**
 * Conversation Messages State: Stores generic conversation message logs
 */
// Ascending by tm_create, then id. The backend orders by tm_create only;
// the id tiebreaker is a LOCAL determinism guard so same-millisecond rows
// render in a stable order (SQUARE-55 design 2.7). See
// utils/timestampCompare.js for why this can't just be a raw string compare.
const compareConversationMessagesAsc = compareByTmCreateThenId;

const conversationMessagesSlice = createSlice({
    name: 'conversationMessages',
    initialState: { data: {} },
    reducers: {
        setWithConversationId: (state, action) => {
            const { conversation_id, data } = action.payload;
            state.data[conversation_id] = data;
        },
        addWithConversationId: (state, action) => {
            const { conversation_id, data } = action.payload;
            const existing = state.data[conversation_id] || [];
            if (data.id && existing.some((m) => m.id === data.id)) return;
            state.data[conversation_id] = [...existing, data];
        },
        // Union by id (payload wins on conflict), never a replace: a websocket
        // row that arrived while a page fetch was in flight must survive the
        // fetch resolving (SQUARE-55 design 2.7). Modelled on mergeWithAIcallID.
        mergeWithConversationId: (state, action) => {
            const { conversation_id, data } = action.payload;
            const existing = state.data[conversation_id] || [];
            const byId = new Map(existing.filter((m) => m && m.id).map((m) => [m.id, m]));
            (Array.isArray(data) ? data : []).forEach((m) => {
                if (m && m.id) byId.set(m.id, m);
            });
            state.data[conversation_id] = Array.from(byId.values()).sort(compareConversationMessagesAsc);
        },
    },
});

export const conversationMessagesReducer = conversationMessagesSlice.reducer;
export const { setWithConversationId, addWithConversationId, mergeWithConversationId } = conversationMessagesSlice.actions;
export const ConversationmessagesSetWithConversationID = (conversationID, data) => ({ type: conversationMessagesSlice.actions.setWithConversationId.type, payload: { conversation_id: conversationID, data } });
export const ConversationmessagesAddWithConversationID = (conversationID, data) => ({ type: conversationMessagesSlice.actions.addWithConversationId.type, payload: { conversation_id: conversationID, data } });
export const ConversationmessagesMergeWithConversationID = (conversationID, data) => ({ type: conversationMessagesSlice.actions.mergeWithConversationId.type, payload: { conversation_id: conversationID, data } });

/**
 * Webchat Messages State: Stores live-delivered webchat messages, indexed
 * by session id, mirroring the conversationMessages slice pattern exactly.
 * message_timeline.js reads this via useSelector and merges/appends
 * (dedup by message id) against its own one-shot initial-history fetch.
 */
const webchatMessagesSlice = createSlice({
    name: 'webchatMessages',
    initialState: { data: {} },
    reducers: {
        addWithSessionId: (state, action) => {
            const { session_id, data } = action.payload;
            const existing = state.data[session_id] || [];
            if (data.id && existing.some((m) => m.id === data.id)) return;
            state.data[session_id] = [...existing, data];
        },
    },
});

export const { addWithSessionId } = webchatMessagesSlice.actions;
export const WebchatmessagesAddWithSessionID = (sessionID, data) => ({ type: webchatMessagesSlice.actions.addWithSessionId.type, payload: { session_id: sessionID, data } });

/**
 * Webchat Session Ended State: Stores a per-session "ended" flag, set when
 * a webchat_session_ended WS event arrives. Minor/optional per design doc
 * §9.3 -- consumed by message_timeline.js to show a "session ended" banner.
 */
const webchatSessionEndedSlice = createSlice({
    name: 'webchatSessionEnded',
    initialState: { data: {} },
    reducers: {
        setEndedWithSessionId: (state, action) => {
            const { session_id } = action.payload;
            state.data[session_id] = true;
        },
    },
});

export const { setEndedWithSessionId } = webchatSessionEndedSlice.actions;
export const WebchatsessionEndedWithSessionID = (sessionID) => ({ type: webchatSessionEndedSlice.actions.setEndedWithSessionId.type, payload: { session_id: sessionID } });

/**
 * Settings State: Stores user preferences including timezone
 */
const settingsSlice = createSlice({
    name: 'settings',
    initialState: {
        timezone: 'auto',
    },
    reducers: {
        setTimezone: (state, action) => {
            state.timezone = action.payload;
        },
    },
});

export const { setTimezone } = settingsSlice.actions;

/**
 * Async Action: Fetch current agent information.
 * Uses the centralized ProviderGet utility to ensure dynamic hostname support.
 */
export const fetchAgentInfo = () => async (dispatch, getState) => {
    // Never refresh auth identity in a delegate tab
    if (getState().delegateSession?.isActive) return
    try {
        // Automatically handles 'v1.0/' and hostname from window._env_ via provider.js
        const agentData = await ProviderGet('me');
        dispatch(setAgentInfo(agentData));
    } catch (error) {
        console.error("Store: Failed to fetch agent info", error);
    }
};

const delegateSessionSlice = createSlice({
  name: 'delegateSession',
  initialState: loadDelegateFromSession(),
  reducers: {
    activate: (state, action) => {
      Object.assign(state, { isActive: true, ...action.payload })
    },
    clear: (state) => {
      // clearAllResourceCaches() MUST run BEFORE removing the delegate_session key
      // (VOIP-1316 design D7): it internally calls isDelegateTab(), which reads this
      // same sessionStorage key to decide whether to also sweep localStorage. If the
      // key were removed first, isDelegateTab() would read null and this reducer would
      // misidentify itself as running outside a delegate tab, wiping localStorage
      // instead of confining the clear to sessionStorage.
      clearAllResourceCaches()
      sessionStorage.removeItem(DELEGATE_SESSION_KEY)
      Object.assign(state, { isActive: false, token: null, agentInfo: null, customerId: null, expire: null })
    },
  },
})

export const DelegateSessionActivate = (payload) => ({ type: delegateSessionSlice.actions.activate.type, payload })
export const DelegateSessionClear    = ()        => ({ type: delegateSessionSlice.actions.clear.type })

export const selectEffectiveAgentInfo = (state) =>
  state.delegateSession?.isActive
    ? state.delegateSession.agentInfo
    : state.auth.agentInfo

export const selectEffectiveToken = (state) =>
  state.delegateSession?.token || state.auth.token

export const selectIsDelegateSession = (state) =>
  state.delegateSession?.isActive === true

export const selectDelegateSession = (state) =>
  state.delegateSession

/**
 * Root Reducer Configuration
 */
const rootReducer = combineReducers({
    ui: uiSlice.reducer,
    theme: themeSlice.reducer,
    aiMessages: aiMessagesSlice.reducer,
    transcripts: transcriptsSlice.reducer,
    auth: authSlice.reducer,
    conversationMessages: conversationMessagesSlice.reducer,
    webchatMessages: webchatMessagesSlice.reducer,
    webchatSessionEnded: webchatSessionEndedSlice.reducer,
    settings: settingsSlice.reducer,
    delegateSession: delegateSessionSlice.reducer,
});

/**
 * Persistence Configuration
 * Whitelisting 'auth' ensures the user stays logged in after page refresh.
 * Whitelisting 'ui' persists sidebar collapse state and section states.
 */
const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['auth', 'theme', 'ui', 'settings'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }),
});

const persistor = persistStore(store);

export { store, persistor };
export default store;